import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../api/axios";

function EditCommunityCenter() {

    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [image, setImage] = useState(null);
    const [video, setVideo] = useState(null);

    const [oldImage, setOldImage] = useState("");
    const [oldVideo, setOldVideo] = useState("");

    const [form, setForm] = useState({
        communityCenterName: "",
        communityCenterLocation: "",
        latitude: "",
        longitude: "",
        areaPerAcre: "",
        ratePerDay: "",
        cleaningCharges: "",
        securityCharges: "",
        gstPercentage: 18,
        address: "",
        description: "",
        contactPerson: "",
        contactNumber: "",
        capacity: "",
        facilities: "",
        isActive: true,
        remarks: ""
    });


    // =====================================
    // FILE URL
    // =====================================

    const getFileUrl = (path) => {

        if (!path) return "";

        if (path.startsWith("http")) {
            return path;
        }

        return `${api.defaults.baseURL?.replace("/api", "")}${path}`;
    };


    // =====================================
    // LOAD DATA
    // =====================================

    const loadCommunityCenter = async () => {

        try {

            setLoading(true);

            const response =
                await api.get(
                    `/CommunityCenter/GetById/${id}`
                );

            const data = response.data;

            setForm({
                communityCenterName:
                    data.communityCenterName || "",

                communityCenterLocation:
                    data.communityCenterLocation || "",

                latitude:
                    data.latitude ?? "",

                longitude:
                    data.longitude ?? "",

                areaPerAcre:
                    data.areaPerAcre ?? "",

                ratePerDay:
                    data.ratePerDay ?? "",

                cleaningCharges:
                    data.cleaningCharges ?? "",

                securityCharges:
                    data.securityCharges ?? "",

                gstPercentage:
                    data.gstPercentage ?? 18,

                address:
                    data.address || "",

                description:
                    data.description || "",

                contactPerson:
                    data.contactPerson || "",

                contactNumber:
                    data.contactNumber || "",

                capacity:
                    data.capacity ?? "",

                facilities:
                    data.facilities || "",

                isActive:
                    data.isActive ?? true,

                remarks:
                    data.remarks || ""
            });

            setOldImage(data.ccImage || "");
            setOldVideo(data.ccVideo || "");

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Error",
                "Community Center data load nahi ho saka",
                "error"
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        if (id) {
            loadCommunityCenter();
        }

    }, [id]);


    // =====================================
    // CHANGE
    // =====================================

    const handleChange = (e) => {

        const {
            name,
            value,
            type,
            checked
        } = e.target;

        setForm(prev => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : value
        }));
    };


    // =====================================
    // CALCULATION
    // =====================================

    const rate =
        Number(form.ratePerDay) || 0;

    const cleaning =
        Number(form.cleaningCharges) || 0;

    const security =
        Number(form.securityCharges) || 0;

    const gstPercentage =
        Number(form.gstPercentage) || 0;

    const subtotal =
        rate +
        cleaning +
        security;

    const gst =
        (subtotal * gstPercentage) / 100;

    const total =
        subtotal + gst;


    // =====================================
    // SUBMIT
    // =====================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        if (!form.communityCenterName.trim()) {

            Swal.fire(
                "Validation",
                "Community Center Name is required",
                "warning"
            );

            return;
        }

        if (!form.communityCenterLocation.trim()) {

            Swal.fire(
                "Validation",
                "Community Center Location is required",
                "warning"
            );

            return;
        }

        if (!form.latitude || !form.longitude) {

            Swal.fire(
                "Validation",
                "Latitude and Longitude are required",
                "warning"
            );

            return;
        }

        if (!form.areaPerAcre) {

            Swal.fire(
                "Validation",
                "Area Per Acre is required",
                "warning"
            );

            return;
        }

        if (!form.ratePerDay) {

            Swal.fire(
                "Validation",
                "Rate Per Day is required",
                "warning"
            );

            return;
        }


        try {

            setSaving(true);

            const data = new FormData();


            data.append(
                "CommunityCenterName",
                form.communityCenterName
            );

            data.append(
                "CommunityCenterLocation",
                form.communityCenterLocation
            );

            data.append(
                "Latitude",
                form.latitude
            );

            data.append(
                "Longitude",
                form.longitude
            );

            data.append(
                "AreaPerAcre",
                form.areaPerAcre
            );

            data.append(
                "RatePerDay",
                form.ratePerDay
            );

            data.append(
                "CleaningCharges",
                form.cleaningCharges || 0
            );

            data.append(
                "SecurityCharges",
                form.securityCharges || 0
            );

            data.append(
                "GSTPercentage",
                form.gstPercentage || 18
            );

            data.append(
                "Address",
                form.address
            );

            data.append(
                "Description",
                form.description
            );

            data.append(
                "ContactPerson",
                form.contactPerson
            );

            data.append(
                "ContactNumber",
                form.contactNumber
            );

            data.append(
                "Capacity",
                form.capacity || ""
            );

            data.append(
                "Facilities",
                form.facilities
            );

            data.append(
                "IsActive",
                form.isActive
            );

            data.append(
                "Remarks",
                form.remarks
            );


            // =================================
            // UPDATED BY
            // =================================

            const userId =
                localStorage.getItem("id");

            if (userId) {

                data.append(
                    "UpdatedBy",
                    userId
                );
            }


            // =================================
            // NEW IMAGE
            // =================================

            if (image) {

                data.append(
                    "image",
                    image
                );
            }


            // =================================
            // NEW VIDEO
            // =================================

            if (video) {

                data.append(
                    "video",
                    video
                );
            }


            await api.put(
                `/CommunityCenter/Update/${id}`,
                data,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data"
                    }
                }
            );


            Swal.fire(
                "Success",
                "Community Center updated successfully",
                "success"
            );


            navigate(
                "/CommunityCenterList"
            );

        } catch (error) {

            console.error(error);

            Swal.fire(
                "Error",
                error.response?.data?.message ||
                "Community Center update nahi ho saka",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    // =====================================
    // LOADING
    // =====================================

    if (loading) {

        return (
            <div className="text-center p-5">

                <div
                    className="spinner-border text-primary"
                />

                <div className="mt-2">
                    Loading...
                </div>

            </div>
        );
    }


    return (
<div className="card shadow">
        <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">
                        Edit Community Center
                    </h4>

                </div>


                <div className="card-body">

                    <form onSubmit={handleSubmit}>

                        <div className="row">


                            {/* NAME */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Community Center Name
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="text"
                                    name="communityCenterName"
                                    value={
                                        form.communityCenterName
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* LOCATION */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Community Center Location
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="text"
                                    name="communityCenterLocation"
                                    value={
                                        form.communityCenterLocation
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* LATITUDE */}

                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Latitude
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="number"
                                    step="any"
                                    name="latitude"
                                    value={form.latitude}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* LONGITUDE */}

                            <div className="col-md-3 mb-3">

                                <label className="form-label">
                                    Longitude
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="number"
                                    step="any"
                                    name="longitude"
                                    value={form.longitude}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* AREA */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Area Per Acre
                                </label>

                                <input
                                    type="number"
                                    step="0.01"
                                    name="areaPerAcre"
                                    value={form.areaPerAcre}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* CAPACITY */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Capacity
                                </label>

                                <input
                                    type="number"
                                    name="capacity"
                                    value={form.capacity}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>

                             {/* FACILITIES */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Facilities
                                </label>

                                <input
                                    type="text"
                                    name="facilities"
                                    value={form.facilities}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>
 {/* ADDRESS */}

                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Address
                                </label>

                                <textarea
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows="2"
                                />

                            </div>

                            {/* RATE */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Rate Per Day
                                    <span className="text-danger">
                                        *
                                    </span>
                                </label>

                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="ratePerDay"
                                    value={form.ratePerDay}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* CLEANING */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Cleaning Charges
                                </label>

                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="cleaningCharges"
                                    value={
                                        form.cleaningCharges
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* SECURITY */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Security Charges
                                </label>

                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    name="securityCharges"
                                    value={
                                        form.securityCharges
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* GST */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    GST %
                                </label>

                                <input
                                    type="number"
                                    step="0.01"
                                    name="gstPercentage"
                                    value={
                                        form.gstPercentage
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* GST AMOUNT */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    GST Amount
                                </label>

                                <input
                                    type="text"
                                    value={gst.toFixed(2)}
                                    readOnly
                                    className="form-control"
                                />

                            </div>


                            {/* TOTAL */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Total Amount
                                </label>

                                <input
                                    type="text"
                                    value={total.toFixed(2)}
                                    readOnly
                                    className="form-control fw-bold"
                                />

                            </div>


                           


                            {/* DESCRIPTION */}

                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Description
                                </label>

                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows="3"
                                />

                            </div>


                            {/* CONTACT PERSON */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Contact Person
                                </label>

                                <input
                                    type="text"
                                    name="contactPerson"
                                    value={form.contactPerson}
                                    onChange={handleChange}
                                    className="form-control"
                                />

                            </div>


                            {/* CONTACT NUMBER */}

                            <div className="col-md-6 mb-3">

                                <label className="form-label">
                                    Contact Number
                                </label>

                                <input
                                    type="text"
                                    name="contactNumber"
                                    value={form.contactNumber}
                                    onChange={handleChange}
                                    className="form-control"
                                    maxLength="10"
                                />

                            </div>


                           


                            {/* OLD IMAGE */}

                            <div className="col-md-2 mb-3">

                                <label className="form-label">
                                    Current Image
                                </label>

                                <div>

                                    {oldImage ? (

                                        <img
                                            src={getFileUrl(oldImage)}
                                            alt="Community Center"
                                            className="img-fluid"
                                            // style={{
                                            //     width: "180px",
                                            //     height: "120px",
                                            //     objectFit: "cover",
                                            //     borderRadius: "8px",
                                            //     border: "1px solid #ddd"
                                            // }}
                                        />

                                    ) : (

                                        <span className="text-muted">
                                            No Image
                                        </span>

                                    )}

                                </div>

                            </div>


                            {/* NEW IMAGE */}

                            <div className="col-md-4 mb-3">

                                <label className="form-label">
                                    Change Image
                                </label>

                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="form-control"
                                    onChange={(e) =>
                                        setImage(
                                            e.target.files?.[0] ||
                                            null
                                        )
                                    }
                                />

                                <small className="text-muted">
                                    Existing image ko change nahi karna
                                    ho to blank chhod dein.
                                </small>

                            </div>


                            {/* OLD VIDEO */}

                            {oldVideo && (

                                <div className="col-md-2 mb-3">

                                    <label className="form-label">
                                        Current Video
                                    </label>

                                    <br />

                                    <video
                                        width="250"
                                        height="150"
                                        controls
                                    >
                                        <source
                                            src={getFileUrl(oldVideo)}
                                            type="video/mp4"
                                        />
                                    </video>

                                </div>

                            )}


                        {/* NEW VIDEO */}
 <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Change Video
                                </label>

                                <input
                                    type="file"
                                    accept="video/mp4,video/webm,video/quicktime"
                                    className="form-control"
                                    onChange={(e) =>
                                        setVideo(
                                            e.target.files?.[0] ||
                                            null
                                        )
                                    }
                                />

                            </div>



                            {/* REMARKS */}

                            <div className="col-md-12 mb-3">

                                <label className="form-label">
                                    Remarks
                                </label>

                                <textarea
                                    name="remarks"
                                    value={form.remarks}
                                    onChange={handleChange}
                                    className="form-control"
                                    rows="2"
                                />

                            </div>


                            {/* ACTIVE */}

                            <div className="col-md-12 mb-3">

                                <div className="form-check">

                                    <input
                                        id="isActive"
                                        type="checkbox"
                                        name="isActive"
                                        checked={form.isActive}
                                        onChange={handleChange}
                                        className="form-check-input"
                                    />

                                    <label
                                        htmlFor="isActive"
                                        className="form-check-label"
                                    >
                                        Active
                                    </label>

                                </div>

                            </div>

                        </div>


                        {/* BUTTONS */}

                        <div className="d-flex gap-2">

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >

                                {saving
                                    ? "Updating..."
                                    : "Update Community Center"
                                }

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() =>
                                    navigate(
                                        "/community-center-list"
                                    )
                                }
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </div>

            </div>

        
    );
}

export default EditCommunityCenter;